import { Box, Button, ButtonGroup, Chip, FormControlLabel, IconButton, Link, Paper, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, ToggleButton, ToggleButtonGroup, Tooltip, Typography, useTheme } from "@mui/material"
import { AppId } from "../../../app/appTypes"
import { useAppDispatch } from "../../../app/hooks"
import { useCallback, useEffect, useMemo, useState } from "react"
import { DocumentTeamMember, loadDocumentTeamMembers } from "./teamMemberApi"
import { unwrapResult } from "@reduxjs/toolkit"
import { ErrorContainer } from "../../../app/ErrorContainer"
import { LoadingContainer } from "../../../app/LoadingContainer"
import { Localized } from "@fluent/react"
import { If } from "../../../app/If"
import { format, parseJSON } from "date-fns";
import DeleteIcon from '@mui/icons-material/Delete';
import MoreTimeIcon from '@mui/icons-material/MoreTime';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import ShareIcon from '@mui/icons-material/Share';
import AddIcon from '@mui/icons-material/Add';
import ScheduleSendIcon from '@mui/icons-material/ScheduleSend';
import produce from "immer"
import { TeamActionStatusLabel } from "./teamMembers/TeamActionStatusLabel"
import { InviteTeamMemberDialog } from "./teamMembers/InviteTeamMemberDialog"
import { DeleteTeamMemberDialog } from "./teamMembers/DeleteTeamMemberDialog"
import { AddEditTeamMemberDialog } from "./teamMembers/AddEditTeamMemberDialog"
import { DocumentContentActions } from "../documentsApi"
import BadgeAvatar from "../../mockups/BadgeAvatar"
import { TeamMemberRoleChip } from "./teamMembers/TeamMemberRoleChip"
import { PrintPreviewTeamView } from "./PrintPreviewTeamView"
import { TeamMemberChangeDeadlineDialog } from "./teamMembers/TeamMemberChangeDeadlineDialog"
import { TeamMemberSendReminderDialog } from "./teamMembers/TeamMemberSendReminderDialog"
import GroupsIcon from '@mui/icons-material/Groups';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';

type DialogState
    = "none"
    | "addEditTeamMember"
    | "changeTMDeadline"
    | "substituteTM"
    | "inviteTeamMember"
    | "sendReminder"
    | "deleteTeamMember"
    | { tag: "confirmTeamMemberDelete", documentId: AppId, teamMemberId: AppId, user: string }


export interface TeamMemberPanelProps {
    documentId: AppId
    actions: DocumentContentActions[],
    enablePreview?: boolean
}

function hasAction(
    actions: DocumentContentActions[],
    action: DocumentContentActions
): boolean {
    return actions.includes(action);
}

export const TeamMemberView = ({ documentId, actions, enablePreview = false }: TeamMemberPanelProps) => {
    const dispatch = useAppDispatch()
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [isError, setIsError] = useState<boolean>(false)
    const [error, setError] = useState(null);
    const [teamMembers, setTeamMembers] = useState<DocumentTeamMember[]>();
    const teamMembersSelection = useMemo(() => new Set(teamMembers?.map(u => u.userName)), [teamMembers])

    const [dialog, setDialog] = useState<DialogState>("none")
    const [isPreview, setIsPreview] = useState(false);

    const reloadTeamMembers = useCallback(() => {
        setIsLoading(true)
        setIsError(false)
        setError(null)

        dispatch(loadDocumentTeamMembers(documentId))
            .then(unwrapResult)
            .then(setTeamMembers)
            .catch((error) => { console.error(error); setIsError(true); setError(error); })
            .finally(() => setIsLoading(false))
    }, [dispatch, documentId])

    useEffect(() => {
        reloadTeamMembers()
    }, [reloadTeamMembers])

    const handleChangeTMDeadlineClick = useCallback((entity: DocumentTeamMember) => {
        setDialog("changeTMDeadline")
    }, [])

    const handleChangeAllTMDeadlineClick = useCallback(() => {
        setDialog("changeTMDeadline")
    }, [])

    const handleSendReminderClick = useCallback(() => {
        setDialog("sendReminder")
    }, [])

    const handleSubstituteTMClick = useCallback((entity: DocumentTeamMember) => {

    }, [])

    const handleDeleteTeamMemberClick = useCallback((entity: DocumentTeamMember, user: string) => {
        setDialog({ tag: "confirmTeamMemberDelete", documentId: documentId, teamMemberId: entity.id, user })

    }, [])

    const handleDeleteTMClick = useCallback((entity: DocumentTeamMember, user: string) => {
    }, [])

    const handleAddTeamMemberClick = useCallback(() => {
        setDialog("addEditTeamMember")
    }, [])

    const handleInviteUser = useCallback(() => {
        setDialog("inviteTeamMember")
    }, [])

    const handleDialogCancelClick = useCallback(() => {
        setDialog("none")
    }, [])

    const handleChangeDeadlineSuccessfulSave = useCallback((deadline: string) => {
        reloadTeamMembers()
        setDialog("none")
    }, [reloadTeamMembers])

    const handleSendReminderSuccessful = useCallback(() => {
        setDialog("none")
    }, [])

    const onTeamMemberSuccessfulSave = (teamMember: DocumentTeamMember) => {
        setTeamMembers(
            produce(draft => {
                if (draft !== undefined) {
                    draft.push(teamMember)
                }
            })
        )
        setDialog("none")
    };

    const onTeamMemberSuccessfulDelete = (teamMemberId: AppId) => {
        setTeamMembers(
            produce(draft => {
                if (draft !== undefined) {
                    const index = draft.findIndex(t => t.id === teamMemberId)
                    if (index >= 0) {
                        draft.splice(index, 1)
                    }
                }
            })
        )
        setDialog("none")
    };


    if (isError) {
        return <>
            <ErrorContainer error={error} />
        </>
    }

    if (isLoading) {
        return <>
            <LoadingContainer />
        </>
    }

    return <>
        {enablePreview && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1, mb: 1 }}>
                <FormControlLabel
                    control={<Switch checked={isPreview} onChange={() => setIsPreview(v => !v)} />}
                    label={<Localized id="print-preview">
                        <span>_preview</span>
                    </Localized>}
                />
            </Box>
        )}
        {enablePreview && isPreview ? (
            <PrintPreviewTeamView teamMembers={teamMembers ?? []} />
        ) : (
            <TableContainer component={Paper} sx={{}}>

                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell colSpan={7} sx={{ bgcolor: "#f9f9f9", fontWeight: "bold" }}>
                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                    <Box sx={{ display: "flex", alignItems: "center" }}>
                                        <GroupsIcon fontSize="medium" sx={{ mr: 1, color: "text.secondary" }} />
                                        <Localized id="document-team">
                                            <span>_team</span>
                                        </Localized>
                                    </Box>
                                    <ToggleButtonGroup size="small" exclusive value={null} aria-label="team-top-actions">
                                        <Tooltip title={<Localized id="urs-change-deadline">deadline</Localized>}>
                                            <ToggleButton value="deadline" aria-label="deadline" onClick={handleChangeAllTMDeadlineClick}>
                                                <MoreTimeIcon fontSize="small" />
                                            </ToggleButton>
                                        </Tooltip>
                                        <Tooltip title={<Localized id="urs-send-reminder">send-reminder</Localized>}>
                                            <ToggleButton value="send-reminder" aria-label="send-reminder" onClick={handleSendReminderClick}>
                                                <ScheduleSendIcon fontSize="small" />
                                            </ToggleButton>
                                        </Tooltip>
                                    </ToggleButtonGroup>
                                </Box>
                            </TableCell>
                        </TableRow>
                        <TableRow >
                            <TableCell>#</TableCell>
                            <TableCell sx={{
                                minWidth: 200,
                            }}>
                                <Localized id="user">
                                    <span>_user</span>
                                </Localized>
                            </TableCell>
                            <TableCell>
                                <Localized id="user-role" vars={{ num: 0 }}>
                                    <span>_rola</span>
                                </Localized>
                            </TableCell>
                            <TableCell sx={{ width: 160, textAlign: "center" }}>
                                <Localized id="opinion-date">
                                    <span>_data zatwierdzenia</span>
                                </Localized>
                            </TableCell>
                            <TableCell sx={{ width: 160, textAlign: "center" }}>
                                <Localized id="approvement-date">
                                    <span>_data zatwierdzenia</span>
                                </Localized>
                            </TableCell>
                            <TableCell sx={{ textAlign: "center", width: 200 }}>
                                <Localized id="status">
                                    <span>_status</span>
                                </Localized>
                            </TableCell>
                            <TableCell sx={{
                                width: 100,
                            }}></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {teamMembers?.map((tm, index) => (
                            <TeamMemberRow
                                editable={true}
                                actions={actions}
                                entity={tm}
                                index={index}
                                key={tm.userName ?? index}
                                onChangeDeadlineClick={handleChangeTMDeadlineClick}
                                onSubstituteTMClick={handleSubstituteTMClick}
                                onDeleteClick={handleDeleteTeamMemberClick}
                            />
                        ))}
                    </TableBody>

                </Table>

            </TableContainer>
        )}
        <Box sx={{
            padding: 1,
        }}>
            <If condition={hasAction(actions, "AddTeamMember")}>
                <Button variant="contained" color="secondary" size="small" startIcon={<AddIcon />} onClick={handleAddTeamMemberClick}>
                    <Localized id="add">
                        <span>_dodaj</span>
                    </Localized>
                </Button>
                <Button sx={{ marginLeft: "5px", marginTop: "1px" }} size="small" variant="outlined" startIcon={<ShareIcon />} onClick={handleInviteUser} >
                    <Localized id="invite">
                        <span>_zaproś</span>
                    </Localized>
                </Button>
            </If>
        </Box>
        {
            dialog === "addEditTeamMember" &&
            <AddEditTeamMemberDialog
                documentId={documentId}
                mode={"create"}
                excludedUsers={teamMembersSelection}
                userToSubstitute={""}
                onCancelClick={handleDialogCancelClick}
                onSuccessfulSave={onTeamMemberSuccessfulSave}
            />
        }
        {dialog === "inviteTeamMember" &&
            <InviteTeamMemberDialog
                documentId={documentId}
                onCancelClick={handleDialogCancelClick}
                onSuccessfulSave={onTeamMemberSuccessfulSave}
            />
        }
        {typeof dialog === 'object' && dialog.tag === "confirmTeamMemberDelete" && <DeleteTeamMemberDialog
            documentId={dialog.documentId}
            teamMemberId={dialog.teamMemberId}
            userFullName={dialog.user}
            onSuccessfulSave={onTeamMemberSuccessfulDelete}
            onCancel={handleDialogCancelClick}
        />
        }
        {dialog === "changeTMDeadline" && (
            <TeamMemberChangeDeadlineDialog
                documentId={documentId}
                onCancel={handleDialogCancelClick}
                onSuccessfulSave={handleChangeDeadlineSuccessfulSave}
            />
        )}
        {dialog === "sendReminder" && (
            <TeamMemberSendReminderDialog
                documentId={documentId}
                onCancel={handleDialogCancelClick}
                onSuccessfulSend={handleSendReminderSuccessful}
            />
        )}
    </>
}

interface TeamMemberRowProps {
    entity: DocumentTeamMember
    actions: DocumentContentActions[]
    index: number
    onChangeDeadlineClick: (entity: DocumentTeamMember) => void
    onSubstituteTMClick: (entity: DocumentTeamMember) => void
    onDeleteClick: (entity: DocumentTeamMember, user: string) => void
    editable: boolean
}

const TeamMemberRow = ({ entity, index, onChangeDeadlineClick, onSubstituteTMClick, onDeleteClick, editable, actions }: TeamMemberRowProps) => {
    const { userName, approvementDate, finishEditionAction, finishEditionDate, opinionDate, opinionAction, approveAction, opinionClosed } = entity
    const { firstName, surname, position, companyName } = entity

    const approvementFormatedDate = approvementDate ? format(parseJSON(approvementDate), "yyyy-MM-dd HH:mm") : ""
    const opinionDateFormatedDate = opinionDate ? format(parseJSON(opinionDate), "yyyy-MM-dd HH:mm") : ""
    const finishEditionFormatedDate = finishEditionDate ? format(parseJSON(finishEditionDate), "yyyy-MM-dd HH:mm") : ""
    const approveDeadline = approveAction?.deadline ? format(parseJSON(approveAction?.deadline), "yyyy-MM-dd") : ""
    const opinionDeadline = opinionAction?.deadline ? format(parseJSON(opinionAction?.deadline), "yyyy-MM-dd") : ""
    const today = new Date();
    const hasSubstitution = entity.substitutionName !== null && entity.substitutionName !== ""
    const hasActiveAction = approveAction !== null || opinionAction !== null || finishEditionAction !== null

    const changedeadlineActive = actions.includes("Changedeadline") !== undefined && hasActiveAction && !hasSubstitution
    const substitutionActive = actions.includes("SubstituteTeamMember") !== undefined && hasActiveAction && !hasSubstitution
    const deleteActive = actions.includes("DeleteTeamMember") === undefined && actions.includes("SubstituteTeamMember") === undefined && editable
    const deleteMemberActive = actions.includes("DeleteTeamMember") !== undefined

    let textStyle = {};
    const theme = useTheme();
    let substitutionRowColor = {};

    if (hasSubstitution) {
        substitutionRowColor = { backgroundColor: theme.palette.grey[200] }
    }

    if (opinionAction?.deadline) {
        const deadlineDate = new Date(opinionAction.deadline);
        today.setDate(today.getDate() + 2);
        if (today > deadlineDate) {
            textStyle = { color: 'red' };
        }
    }

    if (approveAction?.deadline) {
        const deadlineDate = new Date(approveAction.deadline);
        today.setDate(today.getDate() + 2);
        if (today > deadlineDate) {
            textStyle = { color: 'red' };
        }
    }

    const handleDeleteClick = useCallback(() => {
        onDeleteClick(entity, `${firstName} ${surname}`)
    }, [entity, onDeleteClick])

    return <TableRow style={substitutionRowColor}>
        <TableCell>{index + 1}</TableCell>


        <TableCell>
            <Box display="flex" alignItems="center" gap={1}>
                <BadgeAvatar
                    firstName={firstName}
                    surname={surname}
                    userId={entity.id}
                />

                <Box display="flex" flexDirection="column" minWidth={0}>
                    <Typography
                        variant="body2"
                        fontWeight={500}
                        noWrap
                    >
                        {firstName} {surname}
                    </Typography>
                    {entity.isExternal && <Chip
                        icon={<PublicOutlinedIcon />}
                        label={<Localized id="project-external"><span>Zewnętrzny</span></Localized>}
                        size="small"
                        variant="outlined"
                        color="info"
                        sx={{
                            width: "fit-content",
                            mt: 0.2,
                            height: 20,
                            '& .MuiChip-label': {
                                px: 0.75,
                                fontSize: '0.68rem',
                            },
                            '& .MuiChip-icon': {
                                fontSize: 13,
                                ml: 0.625,
                                mr: -0.25,
                            },
                        }}
                    />}

                    {position && (
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            noWrap
                            sx={{ lineHeight: 1.3 }}
                        >
                            {position}
                        </Typography>
                    )}

                    <Typography
                        variant="caption"
                        color="text.secondary"
                        noWrap
                        sx={{
                            lineHeight: 1.3,
                            mt: 0.2,
                            fontWeight: 500,
                        }}
                    >
                        {companyName || '-'}
                    </Typography>

                    {userName && (
                        <Link
                            href={`mailto:${userName}`}
                            underline="none"
                            title={userName}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.4,
                                minWidth: 0,
                                mt: 0.2,
                                color: 'text.disabled',
                                width: 'fit-content',
                                maxWidth: '100%',
                                cursor: 'pointer',

                                '&:hover': {
                                    color: 'primary.main',
                                    textDecoration: 'underline',
                                },
                            }}
                        >
                            <EmailOutlinedIcon
                                sx={{
                                    fontSize: 13,
                                    color: 'inherit',
                                    flexShrink: 0,
                                }}
                            />

                            <Typography
                                variant="caption"
                                noWrap
                                sx={{
                                    lineHeight: 1.3,
                                    fontSize: '0.72rem',
                                    color: 'inherit',
                                }}
                            >
                                {userName}
                            </Typography>
                        </Link>
                    )}
                </Box>
            </Box>
        </TableCell>
        <TableCell>
            <TeamMemberRoleChip role={entity.role} />
        </TableCell>

        <TableCell sx={{ textAlign: "center" }}>
            <If condition={opinionDateFormatedDate !== ""} otherwise={<span>-</span>}>
                {opinionDateFormatedDate}
            </If>
        </TableCell>
        <TableCell sx={{ textAlign: "center" }}>{approvementFormatedDate || '-'}</TableCell>
        <TableCell>
            <If condition={hasSubstitution}>
                <Box display="flex" style={{ textAlign: "center" }} flexDirection="column">
                    <Localized id="urs-team-substituted-by">
                        <span>_Zastąpiony przez </span>
                    </Localized> entity.substitutionName
                </Box>
            </If>
            <If condition={opinionAction != null}>
                <Box display="flex" style={{ textAlign: "center" }} flexDirection="column">
                    <div>
                        <TeamActionStatusLabel status={opinionAction?.status}></TeamActionStatusLabel>
                    </div>
                    <div style={textStyle}>
                        <Localized id="urs-action-deadline">
                            <span>_Termin</span>
                        </Localized> {opinionDeadline}
                    </div>
                </Box>
            </If>
            <If condition={approveAction != null}>
                <Box display="flex" style={{ textAlign: "center" }} flexDirection="column">
                    <div>
                        <TeamActionStatusLabel status={approveAction?.status}></TeamActionStatusLabel>
                    </div>
                    <div style={textStyle}>
                        <Localized id="urs-action-deadline">
                            <span>_Termin</span>
                        </Localized> {approveDeadline}
                    </div>
                </Box>
            </If>
            <If condition={opinionClosed != null}>
                <Box display="flex" style={{ textAlign: "center" }} flexDirection="column">
                    <div>
                        <Localized id="urs-action-no-opinion">
                            <span>_brak opiniowania</span>
                        </Localized>
                    </div>
                </Box>
            </If>
            <If condition={finishEditionAction != null}>
                <Box display="flex" style={{ textAlign: "center" }} flexDirection="column">
                    <div>
                        <TeamActionStatusLabel status={finishEditionAction?.status}></TeamActionStatusLabel>
                    </div>
                </Box>
            </If>
        </TableCell>
        <TableCell>
            <ButtonGroup>
                {/* <If condition={changedeadlineActive}>
                    <Tooltip title={<Localized id="urs-change-deadline">Zmień deadline</Localized>}>
                        <IconButton size="small" onClick={() => onChangeDeadlineClick(entity)}>
                            <MoreTimeIcon />
                        </IconButton>
                    </Tooltip>
                </If>
                <If condition={substitutionActive}>
                    <Tooltip title={<Localized id="urs-appoint-substitution">Wyznacz zastępstwo</Localized>}>
                        <IconButton size="small" onClick={() => onSubstituteTMClick(entity)}>
                            <SwapVertIcon />
                        </IconButton>
                    </Tooltip>
                </If> */}
                <If condition={deleteActive}>
                    <Tooltip title={<Localized id="delete">Usuń</Localized>}>
                        <IconButton size="small" onClick={handleDeleteClick}>
                            <DeleteIcon />
                        </IconButton>
                    </Tooltip>
                </If>
                <If condition={deleteMemberActive}>
                    <Tooltip title={<Localized id="delete">Usuń</Localized>}>
                        <IconButton size="small" onClick={handleDeleteClick}>
                            <DeleteIcon />
                        </IconButton>
                    </Tooltip>
                </If>
            </ButtonGroup>
        </TableCell>
    </TableRow>
}

